import React from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MinusCircle, PlusCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDateTime, toDateInputValue } from '@/lib/format-date';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import {
  AccessCard, KeyDraft, LimitsCard, NameCard, draftToBody,
} from './developer-key-form';

/**
 * Edit an existing key: name, scopes, expiry and rate limit.
 *
 * The secret itself is never touched — there is no way to change or re-reveal
 * it, by design. Rotating the credential means issuing a new key and revoking
 * this one; this page changes what the existing key is *allowed to do*.
 *
 * A revoked key cannot be edited (the server refuses), because bringing one
 * back would defeat the point of revoking it.
 */

interface ApiKeyRow {
  id: string;
  name: string;
  keyLast4: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  rateLimitPerMinute: number | null;
  effectiveRateLimit: number;
  createdBy: { id: string; fullName: string | null } | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export function DeveloperEditKeyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // The list endpoint is the only read surface for keys; one fetch is cheap
  // and keeps the server API surface as small as possible.
  const { data: keys = [], isLoading } = useQuery<ApiKeyRow[]>({
    queryKey: ['/api-keys'],
    queryFn: async () => (await api<ApiKeyRow[]>('/api-keys')).data || [],
  });
  const key = keys.find((k) => k.id === id);

  const [draft, setDraft] = React.useState<KeyDraft | null>(null);
  const original = React.useRef<KeyDraft | null>(null);

  React.useEffect(() => {
    if (!key || draft) return;
    const d: KeyDraft = {
      name: key.name,
      scopes: [...key.scopes],
      expiresAt: key.expiresAt ? toDateInputValue(key.expiresAt) : '',
      rateLimit: key.rateLimitPerMinute != null ? String(key.rateLimitPerMinute) : '',
    };
    setDraft(d);
    original.current = { ...d, scopes: [...d.scopes] };
  }, [key, draft]);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api<any>(`/api-keys/${id}`, { method: 'PATCH', body: JSON.stringify(draftToBody(draft!)) });
      if (!r.success) throw new Error(r.error?.message || 'Failed to save');
      return r.data;
    },
    onSuccess: () => {
      toast('Saved', 'Changes take effect on the integration’s next request', 'success');
      qc.invalidateQueries({ queryKey: ['/api-keys'] });
      navigate('/admin/developer');
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  if (isLoading || (!key && keys.length === 0)) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!key) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold">Key not found</h1>
        <p className="text-sm text-muted-foreground">It may have been deleted.</p>
        <Button variant="outline" onClick={() => navigate('/admin/developer')}>Back to Developer</Button>
      </div>
    );
  }
  if (key.status === 'revoked') {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold">{key.name}</h1>
        <p className="text-sm text-muted-foreground">
          This key was revoked on {fmtDateTime(key.revokedAt)} and cannot be edited — restoring access
          would defeat the point of revoking it. Issue a new key instead.
        </p>
        <Button variant="outline" onClick={() => navigate('/admin/developer')}>Back to Developer</Button>
      </div>
    );
  }
  if (!draft) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const added = draft.scopes.filter((s) => !(original.current?.scopes ?? []).includes(s));
  const removed = (original.current?.scopes ?? []).filter((s) => !draft.scopes.includes(s));
  const dirty = JSON.stringify(draftToBody(draft)) !== JSON.stringify(draftToBody(original.current!));
  const canSave = dirty && draft.name.trim().length > 0 && draft.scopes.length > 0 && !save.isPending;

  /** Spell out the delta — on an edit, what CHANGED matters more than the end state. */
  const changeNote = (added.length > 0 || removed.length > 0) ? (
    <div className="text-xs border rounded-md px-3 py-2 space-y-1">
      {added.map((s) => (
        <div key={s} className="flex items-center gap-1.5">
          <PlusCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-500" />
          <span>Granting <span className="font-mono">{s}</span></span>
        </div>
      ))}
      {removed.map((s) => (
        <div key={s} className="flex items-center gap-1.5">
          <MinusCircle className="h-3.5 w-3.5 text-destructive" />
          <span>
            Removing <span className="font-mono">{s}</span> — calls using it start failing immediately
          </span>
        </div>
      ))}
    </div>
  ) : undefined;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/admin/developer" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Developer
        </Link>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <h1 className="text-2xl font-semibold">{key.name}</h1>
          <span className="font-mono text-sm text-muted-foreground">{key.keyLast4}</span>
          {key.status === 'expired' && <Badge variant="secondary">Expired</Badge>}
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">
          Created {fmtDateTime(key.createdAt)}
          {key.createdBy?.fullName ? ` by ${key.createdBy.fullName}` : ''} · last used {fmtDateTime(key.lastUsedAt, 'never')}.
          The secret cannot be changed or shown again — to replace it, issue a new key and revoke this one.
        </p>
        {key.status === 'expired' && (
          <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
            This key has expired and is refusing requests. Setting a future expiry below brings it back.
          </p>
        )}
      </div>

      <NameCard draft={draft} onChange={setDraft} />

      <div className="grid gap-4 xl:grid-cols-3">
        <AccessCard draft={draft} onChange={setDraft} changeNote={changeNote} />
        <LimitsCard draft={draft} onChange={setDraft} />
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={() => save.mutate()} disabled={!canSave}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="outline" onClick={() => navigate('/admin/developer')} disabled={save.isPending}>
          Cancel
        </Button>
        {!dirty && <span className="text-xs text-muted-foreground">No changes yet</span>}
        {dirty && draft.scopes.length === 0 && (
          <span className="text-xs text-muted-foreground">Select at least one scope</span>
        )}
      </div>
    </div>
  );
}

export default DeveloperEditKeyPage;
